import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AiroService {
  constructor(private readonly http: HttpClient) {}
  private token: string | null = "";
  private readonly AIRO_URL = environment.airoUrl;
  private readonly AIRO_DATA_URL = environment.airoDataUrl;
  private apiKeyHeaders: { [key: string]: string } = {};
  private apiUserCreds: { [key: string]: string } = {};
  private apiKeyLoadPromise: Promise<void> | null = null;

  private async loadAPIKey(): Promise<void> {
    // Check if already loaded
    if (this.apiKeyHeaders["client-id"] && this.apiKeyHeaders["secret-key"]) {
      return;
    }

    // Check localStorage first
    const storedClientId = localStorage.getItem("airo_client_id");
    const storedSecretKey = localStorage.getItem("airo_secret_key");

    if (storedClientId && storedSecretKey) {
      this.apiKeyHeaders = {
        "client-id": storedClientId,
        "secret-key": storedSecretKey,
      };
      return;
    }

    // If not in localStorage, fetch from API
    try {
      const res = await this.http
        .get<{
          "client-id": string;
          "secret-key": string;
        }>("users/get-airo-access-token/")
        .toPromise();

      this.apiKeyHeaders = {
        "client-id": res["client-id"],
        "secret-key": res["secret-key"],
      };

      // Store in localStorage for future use
      localStorage.setItem("airo_client_id", res["client-id"]);
      localStorage.setItem("airo_secret_key", res["secret-key"]);
    } catch (error) {
      console.error("Failed to load API key:", error);
      throw error;
    }
  }

  private getAPIKey(): Observable<{ [key: string]: string }> {
    // If there's no ongoing load, start one
    if (!this.apiKeyLoadPromise) {
      this.apiKeyLoadPromise = this.loadAPIKey();
    }

    // Convert promise to observable
    return from(this.apiKeyLoadPromise).pipe(
      switchMap(() => of(this.apiKeyHeaders))
    );
  }

  private credentialsLoadPromise: Promise<void> | null = null;

  private async loadCredentials(): Promise<void> {
    // Check if already loaded
    if (this.apiUserCreds.email && this.apiUserCreds.password) {
      return;
    }

    try {
      const res = await this.http
        .get<{
          email: string;
          password: string;
        }>("users/get-airo-user-credentials/")
        .toPromise();

      this.apiUserCreds = {
        email: res["email"],
        password: res["password"],
      };
    } catch (error) {
      console.error("Failed to load credentials:", error);
      throw error;
    }
  }

  private getCredentials(): Observable<{ [key: string]: string }> {
    // If there's no ongoing load, start one
    if (!this.credentialsLoadPromise) {
      this.credentialsLoadPromise = this.loadCredentials();
    }

    // Convert promise to observable
    return from(this.credentialsLoadPromise).pipe(
      switchMap(() => of(this.apiUserCreds))
    );
  }

  getAssessmentAiSummary(data: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/assessment/generate-summary`, data, {
          headers,
        })
      )
    );
  }

  getSurveyAiSummary(data: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/survey/generate-summary`, data, {
          headers,
        })
      )
    );
  }

  login(): Observable<any> {
    return this.getCredentials().pipe(
      switchMap((credentials) =>
        this.http.post(`${this.AIRO_URL}/login`, {
          email: credentials.email,
          password: credentials.password,
        })
      )
    );
  }

  verifyToken(): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(`${this.AIRO_URL}/health-check`, {
          headers,
        })
      )
    );
  }

  refreshToken(): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(
          `${this.AIRO_URL}/refresh-token`,
          {},
          {
            headers,
          }
        )
      )
    );
  }

  clearToken() {
    this.token = "";
    localStorage.removeItem("airo_access_token");
    localStorage.removeItem("airo_refresh_token");
  }

  async getToken() {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem("airo_access_token")) {
        this.token = localStorage.getItem("airo_access_token");
        this.verifyToken().subscribe(
          (res: any) => {
            resolve(this.token);
          },
          (error: any) => {
            this.refreshToken().subscribe(
              (res: any) => {
                localStorage.setItem(
                  "airo_access_token",
                  res?.data?.access_token
                );
                this.token = res?.data?.access_token;
                resolve(this.token);
              },
              (err: any) => {
                reject(new Error("Unable to refresh access token"));
              }
            );
          }
        );
      } else {
        this.login().subscribe(
          (res: any) => {
            localStorage.setItem("airo_access_token", res?.data?.access_token);
            localStorage.setItem(
              "airo_refresh_token",
              res?.data?.refresh_token
            );
            this.token = localStorage.getItem("airo_access_token");
            resolve(this.token);
          },
          (error) => {
            reject(new Error("Wrong Credentials"));
          }
        );
      }
    });
  }

  uploadFile(data: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/s3-file-upload`, data, {
          headers,
        })
      )
    );
  }

  fetchFileData(fileIds: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(`${this.AIRO_URL}/files?file_ids=` + fileIds.join(","), {
          headers,
        })
      )
    );
  }

  createConversation(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/conversation`, payload, {
          headers,
        })
      )
    );
  }

  generateFollowUpQuestions(id: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(
          `${this.AIRO_URL}/conversation/${id}/generate-follow-up-questions`,
          {},
          {
            headers,
          }
        )
      )
    );
  }

  getFollowUpQuestions(id: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(
          `${this.AIRO_URL}/conversation/${id}/follow-up-questions`,
          {
            headers,
          }
        )
      )
    );
  }

  getSingleConversation(conversationId: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(`${this.AIRO_URL}/conversation/${conversationId}`, {
          headers,
        })
      )
    );
  }

  intiateConversation(payload: any, id: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/chat/${id}`, payload, {
          headers,
        })
      )
    );
  }

  async chatStreamObservable(url: string, observer: any) {
    // Wait for API key to load
    if (!this.apiKeyLoadPromise) {
      this.apiKeyLoadPromise = this.loadAPIKey();
    }
    await this.apiKeyLoadPromise;

    return fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...this.apiKeyHeaders,
      },
    })
      .then((response) => {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        if (!response.ok) {
          // handle response error
          observer.error();
        }

        function push() {
          return reader?.read().then(({ done, value }) => {
            if (done) {
              observer.complete();
              return;
            }
            //parse text content from response
            const events = decoder.decode(value);
            let content = "";
            for (const element of events) {
              content += element;
            }
            observer.next(content);
            push();
          });
        }

        push();
      })
      .catch((err: Error) => {
        // handle fetch error
        observer.error();
      });
  }

  chatStream(id: string, regenerate = false) {
    let url = this.AIRO_URL;
    if (regenerate) {
      url += `/re-generate/message/${id}`;
    } else {
      url += `/generate-answer/${id}`;
    }
    return new Observable<string>((observer) => {
      this.chatStreamObservable(url, observer);
    });
  }

  detectTopic(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/document-topic-detection`, payload, {
          headers,
        })
      )
    );
  }

  detectPulse(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/detect-pulse`, payload, {
          headers,
        })
      )
    );
  }

  getGDPBVTSummary(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(
          `${this.AIRO_URL}/survey/vendor-wise/generate-summary`,
          payload,
          {
            headers,
          }
        )
      )
    );
  }
  getPreviousConversations(queryType: string): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(
          `${this.AIRO_URL}/conversations?query_type=${queryType}`,
          {
            headers,
          }
        )
      )
    );
  }

  getDirectWebResult(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/direct-query`, payload, {
          headers,
        })
      )
    );
  }

  getWebResult(payload: any): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(`${this.AIRO_URL}/signals-query`, payload, {
          headers,
        })
      )
    );
  }

  getDefaultRepoName(): Observable<any> {
    return this.http.get("app-config/airo_default_repo/");
  }

  getAiroDataConversations(repoId: number): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(
          `${this.AIRO_DATA_URL}/project/${repoId}/conversation?page=1&number_of_records=10`,
          {
            headers,
          }
        )
      )
    );
  }

  generateAnswer(repoId: number, query: string): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.post(
          `${this.AIRO_DATA_URL}/project/${repoId}/generate-answer`,
          { query },
          {
            headers,
          }
        )
      )
    );
  }

  getAnswerResults(
    repoId: number,
    answerId: number,
    params: any
  ): Observable<any> {
    return this.getAPIKey().pipe(
      switchMap((headers) =>
        this.http.get(
          `${this.AIRO_DATA_URL}/project/${repoId}/answer/${answerId}/get-results`,
          {
            headers,
            params,
          }
        )
      )
    );
  }
}
